import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHelp } from '../components/shared/PageHelp'
import { InfoPopover } from '../components/shared/InfoPopover'
import {
  ArrowPathIcon,
  CalculatorIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  DocumentChartBarIcon,
  PresentationChartLineIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0)

const formatDecimalMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 3,
  }).format(Number.isFinite(value) ? value : 0)

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number.isFinite(value) ? value : 0)

const formatPercent = (value: number) => `${Math.round((Number.isFinite(value) ? value : 0) * 100)}%`

const formatCompactMoney = (value: number) => {
  const safeValue = Number.isFinite(value) ? value : 0
  if (Math.abs(safeValue) >= 1000000) return `$${(safeValue / 1000000).toFixed(safeValue >= 10000000 ? 0 : 1)}M`
  if (Math.abs(safeValue) >= 1000) return `$${Math.round(safeValue / 1000)}K`
  return formatMoney(safeValue)
}

const maColors = {
  blue: '#0B63CE',
  blueDark: '#064A9F',
  green: '#2AC769',
  greenSoft: '#ECFDF5',
  slate: '#334155',
  muted: '#64748B',
  line: '#E2E8F0',
}

type Inputs = {
  monthlyMessages: number
  smsShare: number
  smsCost: number
  mmsCost: number
  currentPlatformFee: number
  ambAdoption: number
  ambCost: number
  containmentRate: number
  averageHandleMinutes: number
  agentHourlyCost: number
  conversionUplift: number
  averageOrderValue: number
  implementationCost: number
  analysisMonths: number
  monthlyOptInGrowth: number
  messagesPerUser: number
}

type ScenarioKey = 'conservative' | 'base' | 'growth'
type TabKey = 'calculator' | 'analysis' | 'graphs' | 'sales'

const horizonPresets = [
  { label: '1M', description: 'One month', months: 1 },
  { label: '3M', description: 'Quarter', months: 3 },
  { label: '6M', description: 'Half year', months: 6 },
  { label: '1Y', description: 'One year', months: 12 },
  { label: '2Y', description: 'Two years', months: 24 },
  { label: '3Y', description: 'Three years', months: 36 },
  { label: '5Y', description: 'Five years', months: 60 },
]

const getHorizonLabel = (months: number) => {
  const preset = horizonPresets.find((option) => option.months === months)
  if (preset) return preset.description
  if (months % 12 === 0) return `${months / 12} years`
  return `${months} months`
}

const defaultInputs: Inputs = {
  monthlyMessages: 250000,
  smsShare: 82,
  smsCost: 0.035,
  mmsCost: 0.095,
  currentPlatformFee: 9000,
  ambAdoption: 65,
  ambCost: 0.01,
  containmentRate: 18,
  averageHandleMinutes: 4,
  agentHourlyCost: 38,
  conversionUplift: 1.5,
  averageOrderValue: 82,
  implementationCost: 45000,
  analysisMonths: 12,
  monthlyOptInGrowth: 3,
  messagesPerUser: 6,
}

const scenarios: Record<ScenarioKey, { label: string; description: string; inputs: Partial<Inputs> }> = {
  conservative: {
    label: 'Conservative',
    description: 'Use this when adoption is uncertain or support flows are early.',
    inputs: {
      ambAdoption: 45,
      containmentRate: 10,
      conversionUplift: 0.7,
      implementationCost: 60000,
      monthlyOptInGrowth: 1.5,
    },
  },
  base: {
    label: 'Base',
    description: 'Balanced discovery case for an active messaging buyer.',
    inputs: defaultInputs,
  },
  growth: {
    label: 'Growth',
    description: 'Use this when the brand has strong commerce, support, and wallet use cases.',
    inputs: {
      ambAdoption: 78,
      containmentRate: 26,
      conversionUplift: 2.3,
      implementationCost: 52000,
      monthlyOptInGrowth: 4,
    },
  },
}

const tabs: Array<{ id: TabKey; label: string }> = [
  { id: 'calculator', label: 'Calculator' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'graphs', label: 'Graphs' },
  { id: 'sales', label: 'Sales Plan' },
]

const cycle = [
  {
    label: 'Qualify',
    title: 'Find expensive traffic',
    description: 'Capture SMS/MMS volume, customer care queues, campaign sends, authentication, order status, and rich interaction candidates.',
  },
  {
    label: 'Quantify',
    title: 'Build the business case',
    description: 'Estimate transport savings, support deflection, conversion lift, payback, and break-even timing before the first branded mock.',
  },
  {
    label: 'Mock',
    title: 'Show the buyer their journey',
    description: 'Create branded AMB flows for the highest value workflow using templates, use-case metadata, and brand assets.',
  },
  {
    label: 'Prove',
    title: 'Package executive proof',
    description: 'Combine calculator output, rendered GIF/MP4, and conversation elements into a short business case.',
  },
  {
    label: 'Expand',
    title: 'Prioritize rollout',
    description: 'Rank the next journeys by economic value, integration readiness, and reusable template coverage.',
  },
]

const recommendedPlays = [
  {
    title: 'Service Deflection',
    trigger: 'High SMS support traffic, long queues, or repeated account questions',
    templates: 'Inbox, Quick Reply, List Picker, Authentication',
    proof: 'Lower handle time and richer self-service completion.',
  },
  {
    title: 'Commerce Recovery',
    trigger: 'Abandoned carts, product discovery, delivery updates, and offers',
    templates: 'Rich Link, WebView, Apple Pay, Wallet',
    proof: 'Higher conversion through tappable cards, checkout, and saved passes.',
  },
  {
    title: 'Appointment Flow',
    trigger: 'Scheduling, branch visits, field service, or pickup windows',
    templates: 'Time Picker, Calendar, Maps, Maps Link',
    proof: 'Fewer missed appointments and lower operational friction.',
  },
  {
    title: 'Secure Account Journey',
    trigger: 'OTP, verification, fraud alerts, or document collection',
    templates: 'Authentication, Files, Inbox, App Extension',
    proof: 'Safer branded channel with clearer customer trust signals.',
  },
]

const discoveryQuestions = [
  'Which SMS/MMS journeys are the most expensive today?',
  'Where do customers abandon because the message cannot complete the task?',
  'Which support intents repeat often enough for rich self-service?',
  'What brand assets and backend systems would make the first mock credible?',
  'Who owns the cost center: messaging, care, digital product, or commerce?',
]

function getMonthlySnapshot(inputs: Inputs, monthIndex = 0) {
  const growthMultiplier = Math.pow(1 + Math.max(inputs.monthlyOptInGrowth, 0) / 100, monthIndex)
  const monthlyMessages = inputs.monthlyMessages * growthMultiplier
  const smsMessages = monthlyMessages * (inputs.smsShare / 100)
  const mmsMessages = Math.max(monthlyMessages - smsMessages, 0)
  const currentTransport = smsMessages * inputs.smsCost + mmsMessages * inputs.mmsCost
  const migratingMessages = monthlyMessages * (inputs.ambAdoption / 100)
  const remainingLegacyMessages = Math.max(monthlyMessages - migratingMessages, 0)
  const blendedLegacyCost = inputs.smsCost * (inputs.smsShare / 100) + inputs.mmsCost * (1 - inputs.smsShare / 100)
  const remainingLegacyCost = remainingLegacyMessages * blendedLegacyCost
  const ambTransport = migratingMessages * inputs.ambCost
  const futureTransport = ambTransport + remainingLegacyCost
  const transportSavings = Math.max(currentTransport - futureTransport, 0)
  const containedMessages = migratingMessages * (inputs.containmentRate / 100)
  const agentMinutesSaved = containedMessages * inputs.averageHandleMinutes
  const laborSavings = (agentMinutesSaved / 60) * inputs.agentHourlyCost
  const revenueEligibleMessages = migratingMessages * 0.18
  const incrementalRevenue = revenueEligibleMessages * (inputs.conversionUplift / 100) * inputs.averageOrderValue
  const currentMonthlyCost = currentTransport + inputs.currentPlatformFee
  const futureMonthlyCost = futureTransport + inputs.currentPlatformFee * 0.35
  const monthlyValue = transportSavings + laborSavings + incrementalRevenue + inputs.currentPlatformFee * 0.65

  return {
    smsMessages,
    mmsMessages,
    currentTransport,
    migratingMessages,
    remainingLegacyMessages,
    remainingLegacyCost,
    ambTransport,
    futureTransport,
    transportSavings,
    containedMessages,
    laborSavings,
    incrementalRevenue,
    currentMonthlyCost,
    futureMonthlyCost,
    monthlyValue,
  }
}

function getResults(inputs: Inputs) {
  const monthOne = getMonthlySnapshot(inputs, 0)
  const horizonMonths = Math.max(Math.round(inputs.analysisMonths), 1)
  const horizonValue = Array.from({ length: horizonMonths }, (_, month) => getMonthlySnapshot(inputs, month).monthlyValue).reduce((total, value) => total + value, 0)
  const annualValue = Array.from({ length: 12 }, (_, month) => getMonthlySnapshot(inputs, month).monthlyValue).reduce((total, value) => total + value, 0)
  const paybackMonths = monthOne.monthlyValue > 0 ? inputs.implementationCost / monthOne.monthlyValue : 0
  const roi = inputs.implementationCost > 0 ? (annualValue - inputs.implementationCost) / inputs.implementationCost : 0
  const horizonRoi = inputs.implementationCost > 0 ? (horizonValue - inputs.implementationCost) / inputs.implementationCost : 0
  const breakEvenMonth = Math.max(1, Math.ceil(paybackMonths))
  const netValue = horizonValue - inputs.implementationCost

  return {
    ...monthOne,
    annualValue,
    horizonValue,
    paybackMonths,
    roi,
    horizonRoi,
    breakEvenMonth,
    netValue,
  }
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
  suffix,
  step = 1,
  helper,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  prefix?: string
  suffix?: string
  step?: number
  helper?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-slate-700">{label}</span>
      <div className="flex overflow-hidden rounded-lg border border-slate-300 bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
        {prefix && <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm text-slate-500">{prefix}</span>}
        <input
          type="number"
          min="0"
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          className="min-w-0 flex-1 border-0 px-3 py-2 text-sm outline-none"
        />
        {suffix && <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-sm text-slate-500">{suffix}</span>}
      </div>
      {helper && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
    </label>
  )
}

function MetricCard({
  label,
  value,
  helper,
  tone,
}: {
  label: string
  value: string
  helper: string
  tone: 'blue' | 'emerald' | 'amber' | 'slate'
}) {
  const tones = {
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-white text-slate-900',
  }
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <p className="text-xs font-bold uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-2 text-2xl font-bold">{value}</p>
      <p className="mt-1 text-xs opacity-75">{helper}</p>
    </div>
  )
}

function HorizontalBars({ rows }: { rows: Array<{ label: string; value: number; color: string }> }) {
  const maxValue = Math.max(...rows.map((row) => row.value), 1)
  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <div key={row.label}>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="font-medium text-slate-700">{row.label}</span>
            <span className="text-slate-500">{formatMoney(row.value)}</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${row.color}`} style={{ width: `${Math.max((row.value / maxValue) * 100, 4)}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function EditorialSection({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string
  title: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-lg border border-blue-100 bg-white p-6 shadow-sm">
      {eyebrow && <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">{eyebrow}</p>}
      <h2 className="max-w-4xl text-3xl font-bold leading-tight text-slate-950">{title}</h2>
      <div className="mt-3 max-w-4xl text-base leading-7 text-slate-600">{children}</div>
    </section>
  )
}

function SpendMigrationChart({ inputs }: { inputs: Inputs }) {
  const width = 880
  const height = 410
  const pad = { top: 42, right: 54, bottom: 76, left: 82 }
  const plotHeight = height - pad.top - pad.bottom
  const horizonMonths = Math.max(Math.round(inputs.analysisMonths), 1)
  const horizonSnapshots = Array.from({ length: horizonMonths }, (_, month) => getMonthlySnapshot(inputs, month))
  const currentTransportTotal = horizonSnapshots.reduce((total, snapshot) => total + snapshot.currentTransport, 0)
  const futureTransportTotal = horizonSnapshots.reduce((total, snapshot) => total + snapshot.futureTransport, 0)
  const remainingLegacyTotal = horizonSnapshots.reduce((total, snapshot) => total + snapshot.remainingLegacyCost, 0)
  const ambTotal = horizonSnapshots.reduce((total, snapshot) => total + snapshot.ambTransport, 0)
  const maxValue = Math.max(currentTransportTotal, futureTransportTotal, 1) * 1.18
  const yFor = (value: number) => pad.top + plotHeight - (value / maxValue) * plotHeight
  const barWidth = 170
  const currentX = 270
  const futureX = 620
  const currentStable = remainingLegacyTotal
  const currentMigratingCounterfactual = Math.max(currentTransportTotal - currentStable, 0)
  const futureStable = remainingLegacyTotal
  const futureAmb = ambTotal
  const gridValues = [0, maxValue * 0.33, maxValue * 0.66, maxValue]
  const savings = Math.max(currentTransportTotal - futureTransportTotal, 0)
  const label = horizonMonths === 1 ? 'Month 1' : getHorizonLabel(horizonMonths)
  const savingsSuffix = horizonMonths === 1 ? '/mo' : ''

  const segment = (x: number, value: number, below: number, fill: string) => {
    const y = yFor(value + below)
    return <rect x={x} y={y} width={barWidth} height={Math.max(yFor(below) - y, 0)} fill={fill} />
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-2xl font-bold text-slate-950">
        Where the spend goes <span className="text-blue-700">across the selected horizon.</span>
      </h3>
      <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
        The base of each bar is the spend that remains on SMS/MMS. The top is the migrating cohort:
        slate if it had stayed on SMS/MMS, Messaging Advisory blue once it moves to AMB.
      </p>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-8 h-[28rem] w-full overflow-visible">
        {gridValues.map((value) => (
          <g key={value}>
            <line x1={pad.left} x2={width - pad.right} y1={yFor(value)} y2={yFor(value)} stroke={maColors.line} />
            <text x={pad.left - 12} y={yFor(value) + 5} textAnchor="end" fontSize="15" fill={maColors.muted}>
              {formatCompactMoney(value)}
            </text>
          </g>
        ))}
        <line x1={pad.left} x2={width - pad.right} y1={yFor(0)} y2={yFor(0)} stroke="#CBD5E1" />
        {segment(currentX, currentStable, 0, '#CBD5E1')}
        {segment(currentX, currentMigratingCounterfactual, currentStable, maColors.slate)}
        {segment(futureX, futureStable, 0, '#CBD5E1')}
        {segment(futureX, futureAmb, futureStable, maColors.blue)}
        <text x={currentX + barWidth / 2} y={yFor(currentTransportTotal) - 18} textAnchor="middle" fontSize="30" fontWeight="800" fill="#0F172A">
          {formatCompactMoney(currentTransportTotal)}
        </text>
        <text x={futureX + barWidth / 2} y={yFor(futureTransportTotal) - 18} textAnchor="middle" fontSize="30" fontWeight="800" fill="#0F172A">
          {formatCompactMoney(futureTransportTotal)}
        </text>
        <path
          d={`M ${currentX + barWidth} ${yFor(currentTransportTotal)} H ${futureX - 90} V ${yFor(futureTransportTotal)} H ${futureX}`}
          fill="none"
          stroke={maColors.green}
          strokeWidth="2"
          strokeDasharray="6 5"
        />
        <path d={`M ${futureX - 94} ${yFor(futureTransportTotal) - 2} l 8 16 l 8 -16`} fill={maColors.green} />
        <text x={currentX + barWidth / 2} y={height - 42} textAnchor="middle" fontSize="14" fontWeight="800" letterSpacing="2" fill="#0F172A">
          CURRENT
        </text>
        <text x={currentX + barWidth / 2} y={height - 20} textAnchor="middle" fontSize="15" fill={maColors.muted}>
          {label} spend on SMS/MMS
        </text>
        <text x={futureX + barWidth / 2} y={height - 42} textAnchor="middle" fontSize="14" fontWeight="800" letterSpacing="2" fill="#0F172A">
          AFTER MIGRATION
        </text>
        <text x={futureX + barWidth / 2} y={height - 20} textAnchor="middle" fontSize="15" fill={maColors.muted}>
          {label} spend after AMB
        </text>
      </svg>
      <div className="border-t border-slate-200 pt-5">
        <div className="flex flex-wrap gap-8 text-sm text-slate-600">
          <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-sm bg-slate-300" />Stays on SMS/MMS</span>
          <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-sm bg-slate-700" />SMS/MMS counterfactual</span>
          <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-sm bg-blue-600" />Migrating cohort on AMB</span>
        </div>
        <div className="mt-6 flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-6 py-5">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-green-800">{label} savings</span>
          <span className="text-3xl font-extrabold text-green-700">{formatCompactMoney(savings)}{savingsSuffix}</span>
        </div>
      </div>
    </div>
  )
}

function CompoundSavingsChart({ inputs }: { inputs: Inputs }) {
  const width = 900
  const height = 430
  const pad = { top: 38, right: 34, bottom: 66, left: 88 }
  const plotWidth = width - pad.left - pad.right
  const plotHeight = height - pad.top - pad.bottom
  const maxMonths = 60
  const selectedMonths = Math.min(Math.max(inputs.analysisMonths, 1), maxMonths)
  const totals = Array.from({ length: maxMonths + 1 }, (_, month) => {
    if (month === 0) return 0
    return Array.from({ length: month }, (_, index) => getMonthlySnapshot(inputs, index).monthlyValue).reduce((total, value) => total + value, 0)
  })
  const maxValue = Math.max(...totals, 1)
  const xFor = (month: number) => pad.left + (month / maxMonths) * plotWidth
  const yFor = (value: number) => pad.top + plotHeight - (value / maxValue) * plotHeight
  const linePath = totals.map((value, month) => `${month === 0 ? 'M' : 'L'} ${xFor(month)} ${yFor(value)}`).join(' ')
  const selectedPath = `${linePath
    .split(' ')
    .slice(0, selectedMonths * 3 + 3)
    .join(' ')} L ${xFor(selectedMonths)} ${yFor(0)} L ${xFor(0)} ${yFor(0)} Z`
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => maxValue * ratio)
  const monthOneValue = getMonthlySnapshot(inputs, 0).monthlyValue

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-2xl font-bold text-slate-950">
        Savings <span className="text-blue-700">compound</span> month over month.
      </h3>
      <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
        As more users opt into AMB each month, every additional opted-in user adds to the run rate and each month builds on the last.
      </p>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-8 h-[30rem] w-full overflow-visible">
        {yTicks.map((value) => (
          <g key={value}>
            <line x1={pad.left} x2={width - pad.right} y1={yFor(value)} y2={yFor(value)} stroke={maColors.line} />
            <text x={pad.left - 14} y={yFor(value) + 5} textAnchor="end" fontSize="15" fill={maColors.muted}>
              {formatCompactMoney(value)}
            </text>
          </g>
        ))}
        <path d={selectedPath} fill={maColors.greenSoft} opacity="0.95" />
        <path d={linePath} fill="none" stroke={maColors.blue} strokeWidth="4" strokeLinecap="round" />
        {[0, 12, 24, 36, 48, 60].map((month) => (
          <g key={month}>
            <line x1={xFor(month)} x2={xFor(month)} y1={yFor(0)} y2={yFor(0) + 8} stroke="#CBD5E1" />
            <text x={xFor(month)} y={height - 30} textAnchor="middle" fontSize="15" fill={maColors.muted}>
              {month === 0 ? 'Today' : `Year ${month / 12}`}
            </text>
          </g>
        ))}
        <circle cx={xFor(1)} cy={yFor(monthOneValue)} r="13" fill="#fff" stroke={maColors.blue} strokeWidth="3" />
        <circle cx={xFor(selectedMonths)} cy={yFor(totals[selectedMonths])} r="6" fill="#fff" stroke={maColors.blue} strokeWidth="3" />
        <text x={xFor(1) + 28} y={yFor(monthOneValue) - 20} fontSize="30" fontWeight="800" fill={maColors.blueDark}>
          {formatCompactMoney(monthOneValue)}
        </text>
        <text x={xFor(1) + 28} y={yFor(monthOneValue) - 48} fontSize="13" fontWeight="800" letterSpacing="2" fill={maColors.muted}>
          MONTH 1
        </text>
        <text x={width / 2} y={height - 2} textAnchor="middle" fontSize="16" fontWeight="600" fill={maColors.slate}>
          Months on AMB
        </text>
      </svg>
      <div className="border-t border-slate-200 pt-5">
        <div className="flex flex-wrap gap-8 text-sm text-slate-600">
          <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-sm bg-blue-600" />Cumulative savings curve</span>
          <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-sm bg-green-100" />Selected horizon</span>
        </div>
        <div className="mt-6 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-6 py-5">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-blue-800">Cumulative savings, {getHorizonLabel(selectedMonths)}</span>
          <span className="text-3xl font-extrabold text-blue-700">{formatCompactMoney(totals[selectedMonths])}</span>
        </div>
      </div>
    </div>
  )
}

function StructuralAdvantageChart({ inputs, results }: { inputs: Inputs; results: ReturnType<typeof getResults> }) {
  const width = 860
  const height = 380
  const pad = { top: 36, right: 118, bottom: 62, left: 82 }
  const plotWidth = width - pad.left - pad.right
  const plotHeight = height - pad.top - pad.bottom
  const baseMessages = Math.max(inputs.messagesPerUser, 1)
  const points = [baseMessages, baseMessages * 1.5, baseMessages * 2, baseMessages * 2.5, baseMessages * 3]
  const smsValues = points.map((messagesPerUser) => ({
    x: messagesPerUser,
    y: results.remainingLegacyCost + Math.max(results.currentTransport - results.remainingLegacyCost, 0) * (messagesPerUser / baseMessages),
  }))
  const ambValues = points.map((messagesPerUser) => ({ x: messagesPerUser, y: results.remainingLegacyCost + results.ambTransport }))
  const maxY = Math.max(...smsValues.map((point) => point.y), ...ambValues.map((point) => point.y), 1) * 1.12
  const xFor = (value: number) => pad.left + ((value - baseMessages) / (baseMessages * 2)) * plotWidth
  const yFor = (value: number) => pad.top + plotHeight - (value / maxY) * plotHeight
  const pathFor = (rows: Array<{ x: number; y: number }>) => rows.map((point, index) => `${index === 0 ? 'M' : 'L'} ${xFor(point.x)} ${yFor(point.y)}`).join(' ')
  const areaPath = `${pathFor(smsValues)} L ${xFor(points[points.length - 1])} ${yFor(ambValues[ambValues.length - 1].y)} ${pathFor([...ambValues].reverse()).replace('M', 'L')} Z`

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Chart 3 - The Structural Advantage</p>
      <h3 className="text-2xl font-bold text-slate-950">
        What happens as <span className="text-blue-700">engagement grows.</span>
      </h3>
      <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
        SMS/MMS scales linearly with volume; AMB stays flatter because it is priced around active users rather than every richer interaction.
      </p>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-8 h-[26rem] w-full overflow-visible">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const value = maxY * ratio
          return (
            <g key={ratio}>
              <line x1={pad.left} x2={width - pad.right} y1={yFor(value)} y2={yFor(value)} stroke={maColors.line} />
              <text x={pad.left - 14} y={yFor(value) + 5} textAnchor="end" fontSize="14" fill={maColors.muted}>
                {formatCompactMoney(value)}
              </text>
            </g>
          )
        })}
        <path d={areaPath} fill={maColors.greenSoft} />
        <path d={pathFor(smsValues)} fill="none" stroke={maColors.slate} strokeWidth="4" strokeLinecap="round" />
        <path d={pathFor(ambValues)} fill="none" stroke={maColors.blue} strokeWidth="4" strokeLinecap="round" />
        <line x1={xFor(baseMessages)} x2={xFor(baseMessages)} y1={pad.top - 14} y2={pad.top + plotHeight} stroke="#CBD5E1" strokeDasharray="5 5" />
        <text x={xFor(baseMessages) - 24} y={pad.top - 22} textAnchor="middle" fontSize="13" fontWeight="800" letterSpacing="2" fill={maColors.muted}>
          TODAY
        </text>
        {points.filter((_, index) => index % 2 === 0).map((value) => (
          <text key={value} x={xFor(value)} y={height - 30} textAnchor="middle" fontSize="15" fill={maColors.muted}>
            {Math.round(value)}
          </text>
        ))}
        <circle cx={xFor(points[points.length - 1])} cy={yFor(smsValues[smsValues.length - 1].y)} r="6" fill={maColors.slate} />
        <circle cx={xFor(points[points.length - 1])} cy={yFor(ambValues[ambValues.length - 1].y)} r="6" fill={maColors.blue} />
        <text x={width - pad.right + 18} y={yFor(smsValues[smsValues.length - 1].y) + 6} fontSize="15" fontWeight="800" fill={maColors.slate}>
          SMS/MMS
        </text>
        <text x={width - pad.right + 18} y={yFor(ambValues[ambValues.length - 1].y) + 6} fontSize="15" fontWeight="800" fill={maColors.blue}>
          AMB
        </text>
        <text x={width / 2} y={height - 2} textAnchor="middle" fontSize="16" fontWeight="600" fill={maColors.slate}>
          Messages per user per month
        </text>
      </svg>
    </div>
  )
}

function RoiSensitivityGrid({ inputs }: { inputs: Inputs }) {
  const adoptionRates = [70, inputs.ambAdoption, 100]
  const messageRows = [4, inputs.messagesPerUser, 8]
  const baseMessagesPerUser = Math.max(inputs.messagesPerUser, 1)
  const calculate = (messagesPerUser: number, adoption: number) => {
    const adjustedInputs = {
      ...inputs,
      monthlyMessages: inputs.monthlyMessages * (messagesPerUser / baseMessagesPerUser),
      ambAdoption: adoption,
      analysisMonths: 1,
    }
    return getResults(adjustedInputs).horizonRoi
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Sensitivity - iOS-slice ROI</p>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="grid grid-cols-4 bg-slate-50 text-center text-sm font-bold uppercase tracking-[0.12em] text-slate-500">
          <div />
          {adoptionRates.map((rate, index) => (
            <div key={`${rate}-${index}`} className="border-l border-slate-200 px-4 py-4">
              Opt-in {Math.round(rate)}%{index === 1 ? ' (base)' : ''}
            </div>
          ))}
        </div>
        {messageRows.map((messagesPerUser, rowIndex) => (
          <div key={`${messagesPerUser}-${rowIndex}`} className="grid grid-cols-4 border-t border-slate-200">
            <div className="bg-slate-50 px-5 py-7 text-sm font-bold uppercase tracking-[0.12em] text-slate-500">
              {Math.round(messagesPerUser)} msgs/user{rowIndex === 1 ? ' (base)' : ''}
            </div>
            {adoptionRates.map((rate, colIndex) => {
              const isBase = rowIndex === 1 && colIndex === 1
              return (
                <div key={`${messagesPerUser}-${rate}-${colIndex}`} className={`border-l border-slate-200 px-5 py-7 text-center text-3xl font-extrabold ${isBase ? 'bg-blue-600 text-white' : 'bg-white text-slate-950'}`}>
                  {formatPercent(calculate(messagesPerUser, rate))}
                </div>
              )
            })}
          </div>
        ))}
      </div>
      <p className="mt-5 max-w-5xl text-sm leading-6 text-slate-600">
        Messages-per-user widens the unit-economics gap because AMB stays flatter while SMS/MMS scales with volume.
        Opt-in is a linear scaler, so the center cell remains the base case.
      </p>
    </div>
  )
}

function HorizonSelector({ months, onChange }: { months: number; onChange: (months: number) => void }) {
  return (
    <div className="rounded-lg border border-blue-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-slate-900">Analysis period</p>
          <p className="text-xs text-slate-500">Switch between short-term and multi-year value views.</p>
        </div>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-100">{getHorizonLabel(months)}</span>
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
        {horizonPresets.map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.months)}
            className={`rounded-md px-3 py-2 text-sm font-bold transition ${
              months === option.months ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt-3">
        <NumberField label="Custom months" value={months} onChange={onChange} suffix="mo" step={1} />
      </div>
    </div>
  )
}

export function SalesEngagementContent({ onClose }: { onClose?: () => void } = {}) {
  const navigate = useNavigate()
  const [inputs, setInputs] = useState<Inputs>(defaultInputs)
  const [scenario, setScenario] = useState<ScenarioKey>('base')
  const [activeTab, setActiveTab] = useState<TabKey>('calculator')
  const [copied, setCopied] = useState(false)

  const results = useMemo(() => getResults(inputs), [inputs])
  const horizonLabel = getHorizonLabel(inputs.analysisMonths)
  const isBreakEvenWithinHorizon = results.breakEvenMonth <= inputs.analysisMonths
  const timeCoverageScore = Math.min((inputs.analysisMonths / Math.max(results.breakEvenMonth, 1)) * 25, 25)
  const economicsScore = Math.min(Math.max(results.horizonRoi * 18, 0), 25)
  const workflowScore = Math.min(Math.max((inputs.ambAdoption + inputs.containmentRate) / 2, 0), 35)
  const netValueScore = results.netValue > 0 ? 15 : Math.max(0, 15 + (results.netValue / Math.max(inputs.implementationCost, 1)) * 15)
  const decisionConfidence = Math.round(Math.min(Math.max(timeCoverageScore + economicsScore + workflowScore + netValueScore, 5), 95))
  const decisionTone = isBreakEvenWithinHorizon ? 'emerald' : inputs.analysisMonths <= 6 ? 'amber' : 'blue'
  const decisionRecommendation = isBreakEvenWithinHorizon
    ? `${horizonLabel} covers payback and shows ${formatMoney(results.netValue)} net value. This is strong enough for an executive business case if the assumptions are validated.`
    : `${horizonLabel} does not yet cover the modeled payback window. Use this as an early discovery case and show the longer horizon before quoting ROI.`

  const update = (key: keyof Inputs, value: number) => {
    setInputs((current) => ({ ...current, [key]: key === 'analysisMonths' ? Math.max(Math.round(value), 1) : value }))
  }

  const applyScenario = (key: ScenarioKey) => {
    setScenario(key)
    setInputs((current) => ({ ...current, ...scenarios[key].inputs }))
  }

  const openRoute = (path: string) => {
    onClose?.()
    navigate(path)
  }

  const brief = [
    'Apple Messages for Business Sales Engagement Brief',
    `Monthly message volume: ${formatNumber(inputs.monthlyMessages)} (${formatNumber(results.smsMessages)} SMS / ${formatNumber(results.mmsMessages)} MMS)`,
    `AMB adoption modeled: ${inputs.ambAdoption}% of messages`,
    `Current monthly messaging baseline: ${formatMoney(results.currentMonthlyCost)}`,
    `Future monthly messaging baseline: ${formatMoney(results.futureMonthlyCost)}`,
    `Estimated monthly value: ${formatMoney(results.monthlyValue)}`,
    `Estimated annual value: ${formatMoney(results.annualValue)}`,
    `Selected analysis period: ${horizonLabel}`,
    `Monthly opt-in growth rate: ${inputs.monthlyOptInGrowth}%`,
    `Selected-period value: ${formatMoney(results.horizonValue)}`,
    `Selected-period net value after launch cost: ${formatMoney(results.netValue)}`,
    `Payback: ${results.paybackMonths.toFixed(1)} months; break-even around month ${results.breakEvenMonth}`,
    `Primary value pool: ${formatMoney(results.transportSavings)} transport, ${formatMoney(results.laborSavings)} service deflection, ${formatMoney(results.incrementalRevenue)} revenue lift per month`,
    'Recommended mock package: Inbox entry point, branded support flow, commerce Rich Link/WebView, and one executive-ready rendered GIF/MP4.',
  ].join('\n')

  const copyBrief = async () => {
    try {
      await navigator.clipboard.writeText(brief)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  const costRows = [
    { label: 'Current transport', value: results.currentTransport, color: 'bg-slate-500' },
    { label: 'Future transport', value: results.futureTransport, color: 'bg-blue-500' },
    { label: 'Service deflection value', value: results.laborSavings, color: 'bg-emerald-500' },
    { label: 'Revenue lift', value: results.incrementalRevenue, color: 'bg-amber-500' },
  ]

  return (
    <div className="space-y-6 pb-8">
      <PageHelp
        title="How Sales Engagement works"
        summary="A guided motion that turns messaging spend into a branded business case. Each step builds on the last: model the economics, package executive proof, then send the strongest play into the brand and template flow to render a demo you can share."
        sections={[
          {
            label: 'Start here',
            content:
              'Pick a scenario (Conservative, Base, or Growth) to seed sensible assumptions, then refine the calculator inputs to match the prospect during discovery or live on the call.',
          },
          {
            label: 'Move through the tabs',
            content:
              'Calculator sets the assumptions. Analysis shows the financial breakdown and decision quality. Graphs are built for executive conversation. Sales Plan turns it into a repeatable motion.',
          },
          {
            label: 'Analysis period',
            content:
              'Use the horizon control to switch between short term and multi year views. Every metric, chart, and the executive brief recalculate for the period you select.',
          },
          {
            label: 'Package the proof',
            content:
              'Copy the executive brief into your CRM or a proposal. It captures volume, savings, payback, ROI, and the recommended mock package in one place.',
          },
          {
            label: 'Build the demo',
            content:
              'Create Mock and Open Templates hand off to the brand and use case flow, where the winning play becomes a real branded AMB render you can drop into a deal room.',
          },
        ]}
      />
      <div className="overflow-hidden rounded-lg border border-blue-100 bg-white shadow-sm">
        <div className="h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-green-500" />
        <div className="p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-700 ring-1 ring-blue-100">
                Sales Engagement
              </span>
              <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-green-700 ring-1 ring-green-100">
                Messaging Advisory ROI
              </span>
            </div>
            <h1 className="text-3xl font-bold text-slate-950">Apple Messages for Business Cost Savings Calculator</h1>
            <p className="mt-2 max-w-4xl text-slate-600">
              Model SMS/MMS migration, Apple Messages for Business adoption, support deflection, commerce uplift, payback, and the
              sales enablement steps needed to turn the business case into a branded mock.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => applyScenario('base')} className="btn btn-secondary inline-flex items-center">
              <ArrowPathIcon className="mr-2 h-5 w-5" />
              Reset
            </button>
            <button onClick={() => openRoute('/use-cases?create=1')} className="btn btn-primary inline-flex items-center">
              <ChatBubbleLeftRightIcon className="mr-2 h-5 w-5" />
              Create Mock
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          {(Object.keys(scenarios) as ScenarioKey[]).map((key) => (
            <button
              key={key}
              onClick={() => applyScenario(key)}
              className={`rounded-lg border p-4 text-left transition ${
                scenario === key ? 'border-blue-500 bg-blue-50 shadow-sm ring-1 ring-blue-100' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'
              }`}
            >
              <p className="font-semibold text-slate-950">{scenarios[key].label}</p>
              <p className="mt-1 text-sm text-slate-600">{scenarios[key].description}</p>
            </button>
          ))}
        </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <MetricCard label={`${horizonLabel} value`} value={formatMoney(results.horizonValue)} helper={`${formatMoney(results.monthlyValue)} monthly value`} tone="blue" />
        <MetricCard label="Monthly savings" value={formatMoney(results.currentMonthlyCost - results.futureMonthlyCost)} helper="Baseline cost delta before CX lift" tone="emerald" />
        <MetricCard label="Payback" value={`${results.paybackMonths.toFixed(1)} mo`} helper={`Break-even around month ${results.breakEvenMonth}`} tone="amber" />
        <MetricCard label={`${horizonLabel} ROI`} value={formatPercent(results.horizonRoi)} helper={`${formatMoney(results.netValue)} net after launch cost`} tone="slate" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-md px-4 py-3 text-sm font-semibold transition ${
                activeTab === tab.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <HorizonSelector months={inputs.analysisMonths} onChange={(value) => update('analysisMonths', value)} />

      {activeTab === 'calculator' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Calculator Inputs</h2>
                <p className="text-sm text-slate-500">Adjust prospect-specific assumptions during discovery or live in the sales call.</p>
              </div>
              <CalculatorIcon className="h-6 w-6 text-blue-600" />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <section>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Current Messaging</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <NumberField label="Monthly SMS/MMS messages" value={inputs.monthlyMessages} onChange={(value) => update('monthlyMessages', value)} step={1000} />
                  <NumberField label="SMS share" value={inputs.smsShare} onChange={(value) => update('smsShare', value)} suffix="%" step={1} />
                  <NumberField label="SMS cost" value={inputs.smsCost} onChange={(value) => update('smsCost', value)} prefix="$" step={0.001} helper={formatDecimalMoney(inputs.smsCost)} />
                  <NumberField label="MMS cost" value={inputs.mmsCost} onChange={(value) => update('mmsCost', value)} prefix="$" step={0.001} helper={formatDecimalMoney(inputs.mmsCost)} />
                  <NumberField label="Current platform fee" value={inputs.currentPlatformFee} onChange={(value) => update('currentPlatformFee', value)} prefix="$" step={500} />
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">AMB Migration</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <NumberField label="AMB adoption" value={inputs.ambAdoption} onChange={(value) => update('ambAdoption', value)} suffix="%" step={1} />
                  <NumberField label="AMB cost per message" value={inputs.ambCost} onChange={(value) => update('ambCost', value)} prefix="$" step={0.001} helper={formatDecimalMoney(inputs.ambCost)} />
                  <NumberField label="Containment / deflection" value={inputs.containmentRate} onChange={(value) => update('containmentRate', value)} suffix="%" step={1} />
                  <NumberField label="Handle time saved" value={inputs.averageHandleMinutes} onChange={(value) => update('averageHandleMinutes', value)} suffix="min" step={0.5} />
                  <NumberField label="Loaded agent cost" value={inputs.agentHourlyCost} onChange={(value) => update('agentHourlyCost', value)} prefix="$" suffix="/hr" step={1} />
                </div>
              </section>

              <section className="lg:col-span-2">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Commerce and Launch</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <NumberField label="Conversion uplift" value={inputs.conversionUplift} onChange={(value) => update('conversionUplift', value)} suffix="%" step={0.1} />
                  <NumberField label="Average order value" value={inputs.averageOrderValue} onChange={(value) => update('averageOrderValue', value)} prefix="$" step={1} />
                  <NumberField label="Launch cost" value={inputs.implementationCost} onChange={(value) => update('implementationCost', value)} prefix="$" step={1000} />
                  <NumberField label="Monthly opt-in growth" value={inputs.monthlyOptInGrowth} onChange={(value) => update('monthlyOptInGrowth', value)} suffix="%" step={0.25} />
                  <NumberField label="Messages per user" value={inputs.messagesPerUser} onChange={(value) => update('messagesPerUser', value)} suffix="/mo" step={0.5} />
                </div>
              </section>

            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Executive Brief</h2>
                <p className="text-sm text-slate-500">Copy into CRM, a proposal, or an internal handoff.</p>
              </div>
              <DocumentChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <pre className="max-h-[34rem] overflow-auto rounded-lg bg-slate-950 p-4 text-xs leading-5 text-emerald-100 whitespace-pre-wrap">{brief}</pre>
            <button onClick={copyBrief} className="btn btn-secondary mt-4 inline-flex w-full items-center justify-center">
              {copied ? <CheckCircleIcon className="mr-2 h-5 w-5 text-green-600" /> : <ClipboardDocumentIcon className="mr-2 h-5 w-5" />}
              {copied ? 'Copied' : 'Copy Brief'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'analysis' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
            <h2 className="text-xl font-semibold text-slate-950">Financial Breakdown</h2>
            <p className="mt-1 text-sm text-slate-500">Compare the current SMS/MMS baseline against the AMB migration model.</p>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-500">Current monthly baseline</p>
                <p className="mt-1 text-3xl font-bold text-slate-950">{formatMoney(results.currentMonthlyCost)}</p>
                <p className="mt-2 text-sm text-slate-600">
                  {formatNumber(results.smsMessages)} SMS, {formatNumber(results.mmsMessages)} MMS, and {formatMoney(inputs.currentPlatformFee)} platform fees.
                </p>
              </div>
              <div className="rounded-lg bg-blue-50 p-4">
                <p className="text-sm font-semibold text-blue-700">Future monthly baseline</p>
                <p className="mt-1 text-3xl font-bold text-blue-950">{formatMoney(results.futureMonthlyCost)}</p>
                <p className="mt-2 text-sm text-blue-800">
                  {formatNumber(results.migratingMessages)} conversations migrate to AMB with remaining legacy fallback.
                </p>
              </div>
            </div>
            <div className="mt-6">
              <HorizontalBars rows={costRows} />
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              <MetricCard label={`${horizonLabel} gross value`} value={formatMoney(results.horizonValue)} helper="Before launch cost" tone="blue" />
              <MetricCard label={`${horizonLabel} net value`} value={formatMoney(results.netValue)} helper="After launch cost" tone="emerald" />
              <MetricCard label={`${horizonLabel} ROI`} value={formatPercent(results.horizonRoi)} helper="Selected-period return" tone="slate" />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-950">Decision Quality</h2>
              <InfoPopover title="Decision Quality" align="left" ariaLabel="What is decision quality">
                <p>A confidence read on whether this case is ready to take to an executive.</p>
                <p>It blends the selected period, payback coverage, ROI, net value, adoption, and deflection into one score, then flags whether break even lands inside the period you chose.</p>
              </InfoPopover>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-700">{horizonLabel} confidence</p>
                  <span className="text-sm font-bold text-slate-950">{decisionConfidence}%</span>
                </div>
                <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${decisionTone === 'emerald' ? 'bg-emerald-500' : decisionTone === 'amber' ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${decisionConfidence}%` }}
                  />
                </div>
                <p className="mt-2 text-sm text-slate-500">Changes with the selected period, payback coverage, ROI, net value, adoption, and deflection.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Break-even</p>
                  <p className={`mt-1 text-lg font-bold ${isBreakEvenWithinHorizon ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {isBreakEvenWithinHorizon ? 'Inside period' : 'Outside period'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Month {results.breakEvenMonth}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Net value</p>
                  <p className={`mt-1 text-lg font-bold ${results.netValue >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{formatMoney(results.netValue)}</p>
                  <p className="mt-1 text-xs text-slate-500">{formatPercent(results.horizonRoi)} ROI</p>
                </div>
              </div>
              <div className={`rounded-lg p-4 text-sm ${isBreakEvenWithinHorizon ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
                {decisionRecommendation}
              </div>
              <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-900">
                Validate platform fee reduction, eligible commerce traffic, and implementation owner before quoting the {horizonLabel.toLowerCase()} case externally.
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'graphs' && (
        <div className="space-y-6 rounded-lg border border-slate-200 bg-slate-50 p-5 shadow-sm">
          <EditorialSection eyebrow="iii. Where the money goes" title={<>SMS & MMS migration to Apple Messages <span className="text-blue-700">for Business.</span></>}>
            These charts are built for discovery and executive conversation: explain the spend movement, the compounding value curve,
            and why richer engagement improves the economics over time.
          </EditorialSection>
          <SpendMigrationChart inputs={inputs} />
          <CompoundSavingsChart inputs={inputs} />
          <StructuralAdvantageChart inputs={inputs} results={results} />
          <RoiSensitivityGrid inputs={inputs} />
        </div>
      )}

      {activeTab === 'sales' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-slate-950">Enablement Cycle</h2>
                <InfoPopover title="Enablement Cycle" align="left" ariaLabel="What is the enablement cycle">
                  <p>The repeatable path from a prospect conversation to a rendered demo. Each step feeds the next.</p>
                  <p>Qualify finds expensive traffic, Quantify builds the business case, Mock shows the buyer their branded journey, Prove packages executive proof, and Expand prioritizes the next rollout.</p>
                </InfoPopover>
              </div>
              <PresentationChartLineIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mt-5 space-y-4">
              {cycle.map((step, index) => (
                <div key={step.label} className="flex gap-3">
                  <div className="mt-1 h-8 w-8 flex-shrink-0 rounded-full bg-blue-100 text-center text-sm font-bold leading-8 text-blue-700">
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-blue-700">{step.label}</p>
                    <h3 className="font-semibold text-slate-950">{step.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-950">Recommended Mock Plays</h2>
                  <p className="text-sm text-slate-500">Pick the first rendered demo from the biggest value pool.</p>
                </div>
                <button onClick={() => openRoute('/templates')} className="btn btn-secondary inline-flex items-center">
                  Open Templates
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {recommendedPlays.map((play) => (
                  <div key={play.title} className="rounded-lg border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-950">{play.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{play.trigger}</p>
                    <p className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">{play.templates}</p>
                    <p className="mt-3 text-sm text-slate-500">{play.proof}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-amber-500" />
                <h2 className="text-xl font-semibold text-slate-950">Discovery Questions</h2>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {discoveryQuestions.map((question) => (
                  <div key={question} className="rounded-lg bg-slate-50 p-4 text-sm font-medium text-slate-700">
                    {question}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
        <strong>Workflow note:</strong> this tool is intentionally separate from renderer templates. It helps qualify and package a sales motion, then
        sends the strongest play into the existing brand/use-case/template flow for real rendering.
      </div>
    </div>
  )
}

export default function SalesEngagementPage() {
  return <SalesEngagementContent />
}
